import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { AnimationItem, LottiePlayer } from "lottie-web/build/player/lottie_light";
import cx from "classnames";

import { useMountedRef } from "@core-ui/hooks/UseMountedRef";
import { isEmpty } from "@core-ui/helpers/Utils";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { captureError } from "@core-ui/utils/Error";
import { ElementVisibilitySensor } from "@core-ui/components/ElementVisibilitySensor";

import styles from "./styles.scss";

export interface Props {
    visible: boolean;
    fileName?: string;
    className?: string;
    autoplay?: boolean;
    loop?: boolean | number;
    playOnClick?: boolean;
    playBackwardsOnClick?: boolean;
    onBackwardAnimationComplete?: () => void;
    onPlayComplete?: () => void;
    backwardSpeed: number;
    forwardSpeed: number;
    animationData?: unknown; //lottie json so type not predictable
    onLoopComplete?: () => void;
    onDOMLoaded?: () => void;
    playLottie: boolean;
    // Use `playForward` in conjunction with `playLottie` prop to play the lottie in the
    // forward or backward direction.
    playForward?: boolean;
    playIfVisible: boolean;
    innerRef?: React.RefObject<HTMLButtonElement>;
    onError?: () => void;
    assetsPath?: string;
    frame?: { initial: number; final: number };
    preserveAspect?: string;
    onFrameEventCompletion?: VoidFunction;
    enableFrameEvent?: boolean;
    frameEventBreak?: number;
    isOnFileChangeEnabled?: boolean;
}

export const LOTTIE_CONTAINER_TEST_ID = "lottie-container-test-id";

function LottieAnimation(_props: Props): JSX.Element {
    const props = useMemo(() => {
        const defaultProps = {
            autoplay: true,
            backwardSpeed: 1,
            forwardSpeed: 1,
            loop: true,
            visible: true,
            playLottie: false,
            playIfVisible: true,
            playForward: true,
        };
        return {
            ...defaultProps,
            ..._props,
        };
    }, [_props]);

    const timer = useRef(0);
    const anim = useRef<AnimationItem>();

    const containerRef = useRef<{
        getElementRef: () => HTMLDivElement | null;
    }>(null);

    const isMountedRef = useMountedRef();
    const isPlayedForwardRef = useRef(true);

    const playAnimationIfVisible = useCallback(
        (visible: boolean): void => {
            if (anim == null || !props.playIfVisible) {
                return;
            }

            timer.current = window.requestAnimationFrame(() => {
                visible ? anim?.current?.play() : anim?.current?.stop();
            });
        },
        [props.playIfVisible],
    );

    const forcePlayLottie = useCallback(
        (visible: boolean): void => {
            if (anim?.current == null) {
                return;
            }

            anim?.current?.setDirection(props.playForward ? 1 : -1);
            timer.current = window.requestAnimationFrame(() => {
                visible ? anim?.current?.play() : anim?.current?.stop();
            });
        },
        [props.playForward],
    );

    const getContainer = useCallback(() => {
        if (containerRef.current == null) {
            return null;
        }

        return containerRef.current?.getElementRef();
    }, []);

    const startLottie = useCallback((): void => {
        timer.current = window.requestAnimationFrame(async () => {
            const container = getContainer();
            if (
                !isMountedRef.current ||
                container === null ||
                (props.fileName && props.fileName.length === 0 && isEmpty(props.animationData))
            ) {
                return;
            }

            // in SDK/Web Payments mode not doing dynamic import will load the lottie bundle on load
            // we don't want that
            // on payment page it will get dynamically loaded when LottieAnimation component is rendered
            // For cart pages we defer load this bundle in script.html and not wait for dynamic load
            try {
                const Lottie = (await import("lottie-web/build/player/lottie_light")).default as LottiePlayer;

                anim.current = Lottie?.loadAnimation({
                    container,
                    autoplay: props.autoplay,
                    loop: props.loop,
                    path: props.fileName,
                    animationData: props.animationData,
                    assetsPath: props.assetsPath,
                    renderer: "svg",
                    rendererSettings: {
                        hideOnTransparent: true,
                        progressiveLoad: true,
                        preserveAspectRatio: props.preserveAspect, // Ensures responsive scaling
                    },
                    initialSegment: props.frame ? [props.frame.initial, props.frame.final] : undefined,
                });

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const handleFrameEvent = (event: any) => {
                    if (props.frameEventBreak && event.currentTime >= props.frameEventBreak) {
                        props.onFrameEventCompletion?.();
                        anim?.current?.removeEventListener("enterFrame", handleFrameEvent);
                    }
                };

                // Conditionally add the event listener
                if (props.enableFrameEvent) {
                    anim?.current?.addEventListener("enterFrame", handleFrameEvent);
                }

                anim?.current?.setSpeed(props.forwardSpeed);

                anim?.current?.addEventListener("loopComplete", () => {
                    if (props.onLoopComplete) {
                        props.onLoopComplete();
                    }
                    if (!isPlayedForwardRef.current && props.onBackwardAnimationComplete) {
                        isPlayedForwardRef.current = !isPlayedForwardRef.current;
                        props.onBackwardAnimationComplete();
                    }
                });

                anim?.current?.addEventListener("complete", () => {
                    if (props.onPlayComplete) {
                        props.onPlayComplete();
                    }
                    if (!isPlayedForwardRef.current && props.onBackwardAnimationComplete) {
                        isPlayedForwardRef.current = !isPlayedForwardRef.current;
                        props.onBackwardAnimationComplete();
                    }
                });

                anim?.current?.addEventListener("DOMLoaded", () => {
                    if (props.onDOMLoaded) {
                        props.onDOMLoaded();
                    }
                });
            } catch (err) {
                captureError(err);
                if (typeof props.onError === "function") {
                    props.onError();
                }
            }
        });
    }, [getContainer, isMountedRef, props]);

    const playOnClick = useCallback((): void => {
        if (isMountedRef.current && props.playOnClick) {
            anim.current?.setSpeed(props.forwardSpeed);
            anim.current?.goToAndPlay(0, true);
        }

        isPlayedForwardRef.current = !isPlayedForwardRef.current;

        if (isMountedRef.current && props.playBackwardsOnClick) {
            anim.current?.setDirection(isPlayedForwardRef.current ? 1 : -1);
            anim.current?.setSpeed(isPlayedForwardRef.current ? props.forwardSpeed : props.backwardSpeed);
            anim.current?.play();
        }
    }, [isMountedRef, props.backwardSpeed, props.forwardSpeed, props.playBackwardsOnClick, props.playOnClick]);

    useEffectOnce(() => {
        if (!props.isOnFileChangeEnabled) {
            try {
                startLottie();
            } catch (err) {
                captureError(err);
            }

            return (): void => {
                if (!isEmpty(anim?.current)) {
                    anim?.current?.destroy();
                }
                window.cancelAnimationFrame(timer?.current);
            };
        }
    });

    useEffect(() => {
        if (props.isOnFileChangeEnabled) {
            try {
                if (!isEmpty(anim?.current)) {
                    anim?.current?.destroy();
                    anim.current = undefined;
                }
                startLottie();
            } catch (err) {
                captureError(err);
            }

            return (): void => {
                if (!isEmpty(anim?.current)) {
                    anim?.current?.destroy();
                }
                window.cancelAnimationFrame(timer?.current);
            };
        }
    }, [props.fileName, props.animationData, props.isOnFileChangeEnabled]);

    const containerClass = cx({
        [styles.hidden]: !props.visible,
    });

    if (isMountedRef.current && !props.autoplay) {
        playAnimationIfVisible(props.visible);
    }

    if (isMountedRef.current && props.playLottie) {
        forcePlayLottie(props.visible);
    }

    return (
        <button
            data-testid={LOTTIE_CONTAINER_TEST_ID}
            onKeyDown={playOnClick}
            className={cx(containerClass, styles.container)}
            onClick={playOnClick}
            ref={props.innerRef}
            aria-hidden="true"
        >
            <ElementVisibilitySensor
                ref={containerRef}
                className={props.className}
                onVisibilityChange={playAnimationIfVisible}
            />
        </button>
    );
}

export default memo(LottieAnimation);
